import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Form, Input, Select, Flex, Button } from "antd"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { CustomForm } from "ui/Form"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { AiEnhanceButton } from "ui/AiEnhanceButton"
import { PhoneInput } from "ui/Input/PhoneInput"
import { AddAvatar } from "ui/PersonalInfo/ProfileInformation/AddAvatar"
import { spacings } from "themes/variables"
import styles from "./index.module.css"
import { useSelector } from "react-redux"
import { handleNameKeyDown } from "utils/InputSanitizers"
import { handleLastNameKeyPress, handleLastNameInput, handleLastNameBlur } from "pagesV2/pro/utils/ProUtils"

/**
 * Edit Crew Member Modal - allows editing crew member information
 */
const EditCrewMemberModal = ({ open, onCancel, onSave, crewMember, roles = []}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const {loading: isUpdating} = useSelector((state) => state.crewProfiles.update)

  // State for AI enhancement
  const [showAiModal, setShowAiModal] = useState(false)
  const [aiDescription, setAiDescription] = useState(crewMember?.description || "")
  const [profileDescription, setProfileDescription] = useState("")

  // State for avatar
  const [avatar, setAvatar] = useState(crewMember?.image || "")
  const [photoFile, setPhotoFile] = useState(null)

  // Track if any changes have been made
  const [hasChanges, setHasChanges] = useState(false)

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        onSave({
          ...values,
          description: aiDescription,
          image: avatar,
          photoFile: photoFile
        })
      })
      .catch((error) => {
        console.error("Validation failed:", error)
      })
  }

  const handleCancel = () => {
    form.resetFields()
    setAiDescription(crewMember?.description || "")
    setAvatar(crewMember?.image || "")
    setPhotoFile(null)
    onCancel()
  }

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setPhotoFile(file)
      setHasChanges(true)
      const reader = new FileReader()
      reader.onload = (event) => {
        setAvatar(event.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Initialize form values when modal opens
  useEffect(() => {
    if (open && crewMember) {
      form.setFieldsValue({
        firstName: crewMember.firstName,
        lastName: crewMember.lastName,
        email: crewMember.email,
        phoneNumber: {
          fullNumber: crewMember.phoneNumber || "",
          countryCode: crewMember.phoneCountryCode || "US"
        },
        title: crewMember.title,
        roleId: crewMember.roleId,
        status: crewMember.status,
        description: crewMember.description || ""
      })
      setAiDescription(crewMember.description || "")
      setAvatar(crewMember.image || "")
      setPhotoFile(null)
      setHasChanges(false)
    }
  }, [open, form]) // Removed crewMember from dependencies to prevent re-initialization during update

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        title={t("crewMember.edit.title")}
        width={800}
        centered
        footer={null}
        className={styles.modal}
      >
        <CustomForm
          form={form}
          layout="vertical"
          className={styles.form}
          onValuesChange={() => setHasChanges(true)}
          initialValues={{
            phoneCountryCode: "+1",
            status: "Available"
          }}
        >
          <Flex vertical gap={spacings.XL}>
            {/* Profile Photo */}
            <Flex>
              <AddAvatar
                avatar={avatar}
                handleImageChange={handleImageChange}
              />
            </Flex>

            {/* Name Fields */}
            <Flex gap={spacings.M}>
              <Form.Item
                name="firstName"
                label={t("crewMember.edit.fields.firstName")}
                rules={[
                  {
                    required: true,
                    message: t("crewMember.edit.validation.firstNameRequired")
                  }
                ]}
                className={styles.halfWidth}
              >
                <Input
                  placeholder={t("crewMember.edit.placeholders.firstName")}
                  size="large"
                  onKeyDown={handleNameKeyDown}
                />
              </Form.Item>

              <Form.Item
                name="lastName"
                label={t("crewMember.edit.fields.lastName")}
                rules={[
                  {
                    required: true,
                    message: t("crewMember.edit.validation.lastNameRequired")
                  }
                ]}
                className={styles.halfWidth}
              >
                <Input
                  placeholder={t("crewMember.edit.placeholders.lastName")}
                  size="large"
                  onKeyDown={handleLastNameKeyPress}
                  onInput={handleLastNameInput}
                  onBlur={(e) => handleLastNameBlur(e, form)}
                />
              </Form.Item>
            </Flex>

            {/* Email and Phone */}
            <Flex gap={spacings.M}>
              <Form.Item
                name="email"
                label={t("crewMember.edit.fields.email")}
                rules={[
                  {
                    required: true,
                    message: t("crewMember.edit.validation.emailRequired")
                  },
                  {
                    type: "email",
                    message: t("crewMember.edit.validation.emailInvalid")
                  }
                ]}
                className={styles.halfWidth}
              >
                <Input
                  placeholder={t("crewMember.edit.placeholders.email")}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="phoneNumber"
                label={t("crewMember.edit.fields.phoneNumber")}
                className={styles.halfWidth}
              >
                <PhoneInput
                  initialValue={crewMember?.phoneNumber}
                  initialCountryCode={crewMember?.phoneCountryCode}
                  onChange={(value) => {
                    form.setFieldsValue({ phoneNumber: value })
                  }}
                />
              </Form.Item>
            </Flex>

            {/* Title and Role */}
            <Flex gap={spacings.M}>
              <Form.Item
                name="title"
                label={t("crewMember.edit.fields.title")}
                rules={[
                  {
                    required: true,
                    message: t("crewMember.edit.validation.titleRequired")
                  }
                ]}
                className={styles.halfWidth}
              >
                <Input
                  placeholder={t("crewMember.edit.placeholders.title")}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="roleId"
                label={t("crewMember.edit.fields.role")}
                rules={[
                  {
                    required: true,
                    message: t("crewMember.edit.validation.roleRequired")
                  }
                ]}
                className={styles.halfWidth}
              >
                <Select
                  placeholder={t("crewMember.edit.placeholders.role")}
                  size="large"
                  options={roles.map((role) => ({
                    label: role.name,
                    value: role.id
                  }))}
                />
              </Form.Item>
            </Flex>

            {/* Description with AI Enhancement */}
            <Form.Item
              name="description"
              rules={[
                {
                  required: true,
                  message: t("crewMember.edit.validation.descriptionRequired")
                }
              ]}
              className="mb-0"
            >
              <Flex align="center" justify="space-between" className={`mb-xs ${styles.descriptionLabel}`}>
                <span>{t("crewMember.edit.fields.description")} *</span>
                <AiEnhanceButton
                  text={aiDescription}
                  minLength={25}
                  onClick={() => setShowAiModal(true)}
                />
              </Flex>
              <Input.TextArea
                name="AI Description"
                rows={5}
                placeholder={t("crewMember.edit.placeholders.description")}
                value={aiDescription}
                onChange={(e) => {
                  const value = e.target.value
                  setAiDescription(value)
                  form.setFieldsValue({ description: value })
                  setHasChanges(true)
                }}
              />
            </Form.Item>

            {/* Status */}
            <Form.Item
              name="status"
              label={t("crewMember.edit.fields.status")}
            >
              <Select
                size="large"
                options={[
                  { label: "Active", value: "Available" },
                  { label: "Deactivated", value: "Unavailable" },
                ]}
              />
            </Form.Item>

            {/* Footer Buttons */}
            <Flex gap={spacings.S} justify="flex-start" className={styles.footer}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                color="primary"
                size="large"
                disabled={isUpdating}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="primary"
                onClick={handleSave}
                size="large"
                loading={isUpdating}
                disabled={!hasChanges}
              >
                {t("crewMember.edit.buttons.update")}
              </Button>
            </Flex>
          </Flex>
        </CustomForm>
      </Modal>

      {/* AI Enhancement Modal */}
      {showAiModal && (
        <AiEnhancementModal
          className="ai-enhancement-modal"
          open={showAiModal}
          description={aiDescription}
          setDescription={(desc) => {
            setAiDescription(desc)
            form.setFieldsValue({ description: desc })
            setHasChanges(true)
          }}
          profileDescription={profileDescription}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setShowAiModal(false)
          }}
          onCancel={() => {
            setShowAiModal(false)
            setProfileDescription("")
          }}
          text="Crew Member Description"
          modalTitle={t("pro.ai_enhancement.modal_title")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.refinement_tooltip")}
          improveWithAITooltipTitle={t("pro.ai_enhancement.improve_with_ai_tooltip")}
        />
      )}
    </>
  )
}

EditCrewMemberModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  crewMember: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    phoneCountryCode: PropTypes.string,
    title: PropTypes.string,
    role: PropTypes.string,
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    status: PropTypes.string,
    image: PropTypes.string
  }),
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })
  ),
}

export default EditCrewMemberModal
